/**
 * @file counters.ts
 * @description Часть вида сайта COUNTERS — счётчики Яндекс Метрики и VK
 * Рекламы и правила согласия посетителя (phase19.16, партия Р3г «Счётчики,
 * баннер согласия на cookie, TD-117», v0.19.62; site-builder.md SB48,
 * SB133–SB137; проект данных docs/phases/phase19.16-materials/db-design-r3g.md
 * §2.2, §4, §10; консультация integrations-r3g-counters.md §1, §2, §5;
 * решения §7.29).
 *
 * Зачем в shared: один и тот же формат номера, умолчания и «условия
 * согласия» нужны API (PATCH части, `insertRevision` → `consentVersion`,
 * проверки публикации, site-config), кабинету (поля и подсказки, «Что
 * изменено») и витрине (загрузчик скриптов, удаление cookie при отзыве).
 * Расхождение = посетителя не переспросили, когда условия сменились, или
 * скрипт грузится по номеру, который кабинет не пропустил бы.
 *
 * Хранение: `SiteDraft.counters` (черновик, `{}` — умолчания) и
 * `SiteRevision.counters` (снимок; NULL — версии до Р3г). Вид баннера — в
 * теме (`theme.consentBanner`, consent-banner.ts), не здесь.
 *
 * «SEO-техника» v0.19.65 (SB147; db-design-seo-tech §1.1, §4.5; решение
 * оркестратора §3а п. 4): галочка Метрики «Передавать данные о товарах и
 * заказах» — `metrika.ecommerce`, по умолчанию включена; отсутствие ключа в
 * старых снимках читается как `true`. Входит в условия согласия (SB135).
 *
 * Закрытый список сервисов (SB48): Google Analytics нет. Расширение — запись
 * в `COUNTER_SERVICES` (OCP), а не if-каскад: загрузчик, удаление cookie,
 * категории баннера и подписи читают реестр.
 */

import { z } from "zod";

import { BUILT_IN_CONSENT_TEXT_VERSION, isConsentTextVersion } from "../site-consent/consent-texts.js";
import { consentBannerWarnings, type SiteConsentBanner } from "./consent-banner.js";
import { deepFreeze } from "./deep-freeze.js";
import type { SiteColors } from "./keys.js";
import { SITE_LIMITS } from "./limits.js";

// ── Реестр сервисов ──

/** Сервисы счётчиков в порядке показа (кабинет, баннер, «Что изменено»). */
export const SITE_COUNTER_SERVICES = ["metrika", "vk"] as const;
export type SiteCounterService = (typeof SITE_COUNTER_SERVICES)[number];

/** Категории согласия в окне «Настроить» (SB134): «Необходимые» — всегда, их здесь нет. */
export const SITE_CONSENT_CATEGORIES = ["analytics", "ads"] as const;
export type SiteConsentCategory = (typeof SITE_CONSENT_CATEGORIES)[number];

/** Режим: «Только после согласия» (умолчание, SB133) или «Уведомление». */
export const SITE_COUNTER_MODES = ["consent", "notice"] as const;
export type SiteCounterMode = (typeof SITE_COUNTER_MODES)[number];

export const SITE_COUNTER_MODE_LABELS: Readonly<Record<SiteCounterMode, string>> = {
  consent: "Только после согласия",
  notice: "Уведомление",
};

/** Что удалить на нашем домене при отзыве согласия (integrations-r3g-counters §3). */
export interface SiteCounterCleanup {
  /** Префиксы имён cookie (`_ym` покрывает `_ym_uid`, `_ym_d`, `_ym_visorc`…). */
  readonly cookiePrefixes: readonly string[];
  /** Точные имена cookie. */
  readonly cookieNames: readonly string[];
  /** Префиксы ключей localStorage и sessionStorage. */
  readonly storagePrefixes: readonly string[];
  /** Точные ключи хранилища. */
  readonly storageNames: readonly string[];
}

/** Домены для будущей CSP (справочно, TD-500): пока CSP на витрине нет. */
export interface SiteCounterCsp {
  readonly script: readonly string[];
  readonly connect: readonly string[];
  readonly img: readonly string[];
  /** Кто показывает наш сайт во фрейме (карта кликов, плеер Вебвизора). */
  readonly frameAncestors: readonly string[];
}

export interface SiteCounterServiceInfo {
  readonly key: SiteCounterService;
  /** Название в кабинете и окне «Настроить». */
  readonly label: string;
  /** Короткое название в тексте баннера: «(Яндекс Метрика, VK)» — SB134. */
  readonly shortLabel: string;
  /** Предложный падеж для подсказки «Добавьте в политику ПДн абзац о …» (SB137). */
  readonly aboutLabel: string;
  readonly category: SiteConsentCategory;
  /** Формат номера после `normalizeCounterId` (без пробелов). */
  readonly idPattern: RegExp;
  /** Текст ошибки неверного номера — словами владельца. */
  readonly idError: string;
  /** Узнаваемый чужой формат и подсказка, что ввести вместо него. */
  readonly idHint: { readonly pattern: RegExp; readonly message: string } | null;
  /** Скрипт сервиса: вставляется динамически, `async`, только после согласия. */
  readonly scriptSrc: string;
  readonly cleanup: SiteCounterCleanup;
  readonly csp: SiteCounterCsp;
}

/** Национальные хосты Метрики (справка «install-counter-csp», 2026-10-23). */
const METRIKA_HOSTS = [
  "mc.yandex.ru",
  ...["az", "by", "co.il", "com", "com.am", "com.ge", "com.tr", "ee", "fr", "kg", "kz", "lt", "lv", "md", "tj", "tm", "uz"].map(
    (zone) => `mc.yandex.${zone}`,
  ),
  "mc.webvisor.com",
  "mc.webvisor.org",
];

/**
 * Реестр сервисов. Форматы номеров, адреса скриптов и списки cookie
 * проверены integrations-external 2026-10-23 по справке Метрики, справке
 * VK Рекламы / Top.Mail.Ru и коду `code.js` 60.6.0 (integrations-r3g-counters
 * §1, §2, §5). Номер счётчика — не секрет: он виден в коде страницы.
 */
export const COUNTER_SERVICES: Readonly<Record<SiteCounterService, SiteCounterServiceInfo>> = deepFreeze({
  metrika: {
    key: "metrika",
    label: "Яндекс Метрика",
    shortLabel: "Яндекс Метрика",
    aboutLabel: "Яндекс Метрике",
    category: "analytics",
    // В API Метрики номер — integer; длина в справке не указана (старые — 8 цифр, новые — 9).
    idPattern: /^[1-9]\d{3,10}$/u,
    idError: "Номер счётчика Метрики — только цифры, от 4 до 11 знаков, без нуля в начале.",
    idHint: null,
    scriptSrc: "https://mc.yandex.ru/metrika/tag.js",
    cleanup: {
      cookiePrefixes: ["_ym"],
      cookieNames: ["gdpr"],
      storagePrefixes: ["_ym", "ytm_"],
      storageNames: ["zz"],
    },
    csp: {
      script: [...METRIKA_HOSTS.map((h) => `https://${h}`), "https://yastatic.net"],
      connect: [
        ...METRIKA_HOSTS.map((h) => `https://${h}`),
        ...METRIKA_HOSTS.map((h) => `wss://${h}`),
        "https://yastatic.net",
      ],
      img: [...METRIKA_HOSTS.map((h) => `https://${h}`), "https://yastatic.net"],
      // В справке 25 хостов интерфейса Метрики; полный список — при вводе CSP (TD-500).
      frameAncestors: ["https://metrika.yandex.ru", "https://metrica.yandex.com"],
    },
  },
  vk: {
    key: "vk",
    label: "VK Реклама",
    shortLabel: "VK",
    aboutLabel: "VK Рекламе",
    category: "ads",
    // Пиксель VK Рекламы — счётчик Top.Mail.Ru, «ID счетчика» числом (решение §7.29 п. 4).
    idPattern: /^[1-9]\d{3,10}$/u,
    idError: "Номер пикселя VK Рекламы — только цифры, от 4 до 11 знаков, без нуля в начале.",
    idHint: {
      pattern: /^VK-RTRG-/iu,
      message:
        "Это пиксель старого кабинета ВКонтакте. Нужен числовой ID из ads.vk.com → «Конверсии» → «Пиксели».",
    },
    scriptSrc: "https://top-fwz1.mail.ru/js/code.js",
    cleanup: {
      cookiePrefixes: ["tmr_"],
      cookieNames: [],
      storagePrefixes: ["tmr_"],
      storageNames: [],
    },
    csp: {
      script: ["https://top-fwz1.mail.ru", "https://privacy-cs.mail.ru", "https://mytopf.com"],
      connect: ["https://top-fwz1.mail.ru", "https://privacy-cs.mail.ru", "https://mytopf.com"],
      // ad.mail.ru — по коду code.js, в документации не указан.
      img: ["https://top-fwz1.mail.ru", "https://privacy-cs.mail.ru", "https://mytopf.com", "https://ad.mail.ru"],
      frameAncestors: [],
    },
  },
});

/** Классы скрытия Вебвизора (справка Метрики «webvisor/settings», 2026-10-23). */
export const SITE_WEBVISOR_PRIVACY_CLASSES = deepFreeze({
  /** Содержимое поля пишется звёздочками. */
  disableKeys: "ym-disable-keys",
  /** Элемент не пишется: текст размыт, картинки серые. */
  hideContent: "ym-hide-content",
} as const);

/**
 * Параметры `ym(id, "init", …)` кроме `webvisor` (integrations-r3g-counters §1):
 * `defer: true` — первый и последующие просмотры отправляются вручную `hit`
 * с очищенным адресом (`sanitizeCounterUrl`).
 */
export const METRIKA_INIT_BASE = deepFreeze({
  defer: true,
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
} as const);

/** Всё, что удалить при отзыве согласия, по всем сервисам — без повторов. */
export function counterCleanupAll(): SiteCounterCleanup {
  const uniq = (pick: (c: SiteCounterCleanup) => readonly string[]): string[] => [
    ...new Set(SITE_COUNTER_SERVICES.flatMap((s) => pick(COUNTER_SERVICES[s].cleanup))),
  ];
  return {
    cookiePrefixes: uniq((c) => c.cookiePrefixes),
    cookieNames: uniq((c) => c.cookieNames),
    storagePrefixes: uniq((c) => c.storagePrefixes),
    storageNames: uniq((c) => c.storageNames),
  };
}

// ── Номер счётчика ──

/** Номер к виду для проверки: без пробелов по краям и внутри (в том числе неразрывных). */
export function normalizeCounterId(raw: string): string {
  return raw.replace(/\s+/gu, "");
}

/** Ошибка номера: null — номер верный (после `normalizeCounterId`). */
export function counterIdError(service: SiteCounterService, raw: string): string | null {
  const info = COUNTER_SERVICES[service];
  const id = normalizeCounterId(raw);
  if (info.idPattern.test(id)) return null;
  if (info.idHint !== null && info.idHint.pattern.test(id)) return info.idHint.message;
  return info.idError;
}

// ── Вступление владельца ──

/** Разметка и ссылки: угловые скобки, адреса и «www.» (простой текст, SB134). */
const INTRO_MARKUP = /[<>]|https?:\/\/|www\./iu;

/**
 * Есть ли в тексте разметка или ссылка. Одно правило для вступления
 * владельца и текстов платформы (`validateConsentTexts`).
 */
export function consentTextHasMarkup(text: string): boolean {
  return INTRO_MARKUP.test(text);
}

/** Длина в кодовых точках (как считают поля кабинета и `SITE_LIMITS`). */
const codePoints = (text: string): number => [...text].length;

export type SiteConsentIntroIssue = "too_long" | "markup";

/** Проблема вступления (после trim) или null. */
export function consentIntroIssue(intro: string): SiteConsentIntroIssue | null {
  const text = intro.trim();
  if (codePoints(text) > SITE_LIMITS.consentIntro) return "too_long";
  if (consentTextHasMarkup(text)) return "markup";
  return null;
}

// ── Схема ──

const counterId = (service: SiteCounterService) =>
  z.string().regex(COUNTER_SERVICES[service].idPattern, COUNTER_SERVICES[service].idError);

/**
 * Часть COUNTERS — строгая схема (то, что пишет кабинет; чтение сохранённого
 * — `readCounters`). Сервис `null` — выключен. Вступление `""` — вступление
 * платформы. `platformTextVersion` — редакция фраз платформы, с которой
 * опубликован баннер (db-design-r3g §2.2): старые снимки читаются как 1.
 */
export const siteCountersSchema = z.strictObject({
  metrika: z
    .strictObject({
      id: counterId("metrika"),
      webvisor: z.boolean(),
      // SB147: `default` — вкладка кабинета, открытая до выкладки v0.19.65, шлёт
      // метрику без ключа и не должна получать 422 (db-design-seo-tech §1.1).
      ecommerce: z.boolean().default(true),
    })
    .nullable(),
  vk: z.strictObject({ id: counterId("vk") }).nullable(),
  mode: z.enum(SITE_COUNTER_MODES),
  noticeWithoutCounters: z.boolean(),
  intro: z
    .string()
    .refine((v) => v === v.trim(), "Вступление — без пробелов по краям")
    .refine((v) => consentIntroIssue(v) !== "too_long", `Вступление — не длиннее ${SITE_LIMITS.consentIntro} знаков`)
    .refine((v) => consentIntroIssue(v) !== "markup", "Вступление — простой текст, без ссылок и угловых скобок"),
  // Существование редакции проверяет API по базе (422 site_counters_text_version_unknown).
  platformTextVersion: z.number().int().refine(isConsentTextVersion, "Номер редакции текста — целое от 1"),
});

/**
 * Часть COUNTERS. Тип — вход схемы (`z.input`), а не выход: у
 * `metrika.ecommerce` есть умолчание, и в типе ключ необязателен —
 * отсутствие значит «передавать» (SB147), как у старых снимков. Так
 * объекты, собранные до v0.19.65 (`{ id, webvisor }`), остаются верными;
 * `readCounters` и схема записи ставят ключ всегда. Читать галочку —
 * только через `metrikaEcommerceEnabled`.
 */
export type SiteCounters = z.input<typeof siteCountersSchema>;

/**
 * Умолчания платформы = «как до Р3г»: счётчиков нет, уведомление «Понятно»
 * с прежним текстом. Режим — «после согласия», Вебвизор при включении
 * Метрики — выключен (решения 2026-10-19). Версия текста — 1, а не текущая:
 * `{}` в базе (магазин ничего не настраивал) читается как версия 1.
 */
export const DEFAULT_SITE_COUNTERS: SiteCounters = deepFreeze({
  metrika: null,
  vk: null,
  mode: "consent",
  noticeWithoutCounters: true,
  intro: "",
  platformTextVersion: BUILT_IN_CONSENT_TEXT_VERSION,
});

/**
 * Новый черновик части: копия умолчаний с текущей редакцией текста
 * платформы. Текущую знает только база (`SiteConsentTextRevision`), поэтому
 * номер — параметром.
 */
export function newSiteCounters(currentTextVersion: number): SiteCounters {
  return { ...DEFAULT_SITE_COUNTERS, platformTextVersion: currentTextVersion };
}

/**
 * Передаёт ли сайт данные электронной коммерции в Метрику (SB147): Метрика
 * включена и галочка не снята. Нет ключа — `true` (умолчание и старые снимки).
 */
export function metrikaEcommerceEnabled(counters: Pick<SiteCounters, "metrika">): boolean {
  return counters.metrika !== null && counters.metrika.ecommerce !== false;
}

/** Включённые сервисы в порядке реестра. */
export function enabledCounterServices(counters: Pick<SiteCounters, "metrika" | "vk">): SiteCounterService[] {
  return SITE_COUNTER_SERVICES.filter((service) => counters[service] !== null);
}

/** Категории согласия, которые есть на сайте (по включённым сервисам), в порядке реестра. */
export function enabledConsentCategories(counters: Pick<SiteCounters, "metrika" | "vk">): SiteConsentCategory[] {
  const enabled = new Set(enabledCounterServices(counters).map((s) => COUNTER_SERVICES[s].category));
  return SITE_CONSENT_CATEGORIES.filter((c) => enabled.has(c));
}

// ── Чтение сохранённого (никогда не бросает) ──

export interface SiteCountersReadIssue {
  /** Путь в части: `metrika.id`, `mode`; пусто — вся часть. */
  path: string;
  reason: string;
}

export interface SiteCountersReadResult {
  counters: SiteCounters;
  replaced: SiteCountersReadIssue[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Номер из сохранённого: строка или число → строка без пробелов; пусто/нет → null. */
function rawCounterId(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;
  const id = normalizeCounterId(value);
  return id === "" ? null : id;
}

/**
 * Сервис из сохранённого: `null`/нет — выключен; объект с пустым номером
 * (`{ id: null }` черновика проекта) — выключен без замечания; неверный
 * номер — выключен с замечанием (скрипт по нему не грузим).
 */
function readServiceId(
  service: SiteCounterService,
  raw: unknown,
  replaced: SiteCountersReadIssue[],
): { id: string; source: Record<string, unknown> } | null {
  if (raw === null || raw === undefined) return null;
  if (!isPlainObject(raw)) {
    replaced.push({ path: service, reason: "Сервис не прочитан — выключен" });
    return null;
  }
  const id = rawCounterId(raw["id"]);
  if (id === null) return null;
  if (!COUNTER_SERVICES[service].idPattern.test(id)) {
    replaced.push({ path: `${service}.id`, reason: "Неверный номер счётчика — сервис выключен" });
    return null;
  }
  return { id, source: raw };
}

/**
 * Чтение части COUNTERS из базы, снимка ревизии, копии или импорта: битое
 * поле заменяется умолчанием по одному, остальное сохраняется. Подъём:
 *   - ключи раннего замысла `yandexMetrikaId` / `vkPixelId` (комментарий
 *     схемы Р1, данных с ними нет) — в `metrika` / `vk`;
 *   - `mode: "after_consent"` (имя проекта данных) — `"consent"`;
 *   - нет `platformTextVersion` — 1 (снимки до появления поля).
 * Номер редакции проверяется только по формату (целое ≥ 1): редакции живут
 * в базе, и номер без строки (импорт с другой платформы) API при сборке
 * заменяет текущей редакцией с предупреждением (db-design-r3g-texts §6).
 */
export function readCounters(raw: unknown): SiteCountersReadResult {
  const replaced: SiteCountersReadIssue[] = [];
  if (raw !== null && raw !== undefined && !isPlainObject(raw)) {
    replaced.push({ path: "", reason: "Счётчики не прочитаны — взяты умолчания" });
  }
  const src: Record<string, unknown> = isPlainObject(raw) ? { ...raw } : {};
  if (src["metrika"] === undefined && src["yandexMetrikaId"] !== undefined) {
    src["metrika"] = { id: src["yandexMetrikaId"], webvisor: false };
  }
  if (src["vk"] === undefined && src["vkPixelId"] !== undefined) {
    src["vk"] = { id: src["vkPixelId"] };
  }

  const metrikaRaw = readServiceId("metrika", src["metrika"], replaced);
  const vkRaw = readServiceId("vk", src["vk"], replaced);

  let mode: SiteCounterMode = DEFAULT_SITE_COUNTERS.mode;
  const rawMode = src["mode"] === "after_consent" ? "consent" : src["mode"];
  if ((SITE_COUNTER_MODES as readonly unknown[]).includes(rawMode)) mode = rawMode as SiteCounterMode;
  else if (rawMode !== undefined) replaced.push({ path: "mode", reason: "Неизвестный режим — «Только после согласия»" });

  const rawNotice = src["noticeWithoutCounters"];
  const noticeWithoutCounters = typeof rawNotice === "boolean" ? rawNotice : DEFAULT_SITE_COUNTERS.noticeWithoutCounters;
  if (rawNotice !== undefined && typeof rawNotice !== "boolean") {
    replaced.push({ path: "noticeWithoutCounters", reason: "Не прочитано — взято умолчание" });
  }

  let intro = "";
  const rawIntro = src["intro"];
  if (typeof rawIntro === "string") {
    const trimmed = rawIntro.trim();
    // Битое вступление не обрезаем (обрезка может исказить смысл) — берётся вступление платформы.
    if (consentIntroIssue(trimmed) === null) intro = trimmed;
    else replaced.push({ path: "intro", reason: "Вступление не прошло проверку — взято вступление платформы" });
  } else if (rawIntro !== undefined && rawIntro !== null) {
    replaced.push({ path: "intro", reason: "Вступление не прочитано — взято вступление платформы" });
  }

  let platformTextVersion = DEFAULT_SITE_COUNTERS.platformTextVersion;
  const rawVersion = src["platformTextVersion"];
  if (isConsentTextVersion(rawVersion)) platformTextVersion = rawVersion;
  else if (rawVersion !== undefined) {
    replaced.push({ path: "platformTextVersion", reason: "Неверный номер редакции текста — взята редакция 1" });
  }

  return {
    counters: {
      metrika:
        metrikaRaw === null
          ? null
          : {
              id: metrikaRaw.id,
              webvisor: metrikaRaw.source["webvisor"] === true,
              // SB147: нет ключа (снимки до v0.19.65) — «передавать»; выключает только явный false.
              ecommerce: metrikaRaw.source["ecommerce"] !== false,
            },
      vk: vkRaw === null ? null : { id: vkRaw.id },
      mode,
      noticeWithoutCounters,
      intro,
      platformTextVersion,
    },
    replaced,
  };
}

// ── Проверка записи (PATCH части) ──

/** Коды 422 части COUNTERS (db-design-r3g §4). Запрет режима и галочку о риске проверяет API. */
export type SiteCountersInputErrorCode =
  | "site_counters_invalid"
  | "site_counters_metrika_id_invalid"
  | "site_counters_vk_id_invalid"
  | "site_counters_intro_too_long"
  | "site_counters_intro_markup";

export interface SiteCountersInputError {
  code: SiteCountersInputErrorCode;
  path: string;
  message: string;
}

export type SiteCountersInputResult =
  | { ok: true; counters: SiteCounters }
  | { ok: false; errors: SiteCountersInputError[] };

const ID_ERROR_CODE: Readonly<Record<SiteCounterService, SiteCountersInputErrorCode>> = {
  metrika: "site_counters_metrika_id_invalid",
  vk: "site_counters_vk_id_invalid",
};

/**
 * Проверка того, что прислал кабинет: номера приводятся к виду без пробелов,
 * вступление — без пробелов по краям; затем строгая схема (лишний ключ —
 * ошибка). Ошибки — с кодами для ответа `422 { error: { code, details } }`.
 */
export function validateSiteCountersInput(input: unknown): SiteCountersInputResult {
  if (!isPlainObject(input)) {
    return { ok: false, errors: [{ code: "site_counters_invalid", path: "", message: "Ожидался объект настроек счётчиков" }] };
  }
  const next: Record<string, unknown> = { ...input };
  const errors: SiteCountersInputError[] = [];
  for (const service of SITE_COUNTER_SERVICES) {
    const value = next[service];
    if (!isPlainObject(value) || typeof value["id"] !== "string") continue;
    const id = normalizeCounterId(value["id"]);
    next[service] = { ...value, id };
    const error = counterIdError(service, id);
    if (error !== null) errors.push({ code: ID_ERROR_CODE[service], path: `${service}.id`, message: error });
  }
  if (typeof next["intro"] === "string") {
    const intro = next["intro"].trim();
    next["intro"] = intro;
    const issue = consentIntroIssue(intro);
    if (issue === "too_long") {
      errors.push({
        code: "site_counters_intro_too_long",
        path: "intro",
        message: `Вступление — не длиннее ${SITE_LIMITS.consentIntro} знаков`,
      });
    } else if (issue === "markup") {
      errors.push({
        code: "site_counters_intro_markup",
        path: "intro",
        message: "Вступление — простой текст, без ссылок и угловых скобок",
      });
    }
  }
  if (errors.length > 0) return { ok: false, errors };
  const parsed = siteCountersSchema.safeParse(next);
  if (parsed.success) return { ok: true, counters: parsed.data };
  return {
    ok: false,
    errors: parsed.error.issues.map((issue) => ({
      code: "site_counters_invalid" as const,
      path: issue.path.map(String).join("."),
      message: issue.message,
    })),
  };
}

// ── Условия согласия (SB135) ──

/**
 * Отпечаток условий согласия — строка с фиксированным порядком ключей
 * (SB135). Всегда входят: набор включённых сервисов (без номеров), Вебвизор,
 * «Без счётчиков показывать уведомление» и версия текста платформы. Режим и
 * вступление входят, только когда включён хотя бы один счётчик: без
 * счётчиков посетитель видит прежнее уведомление с текстом платформы, и
 * невидимые ему изменения не должны переспрашивать «Понятно» (решение
 * оркестратора 2026-10-23). Смена номера у того же сервиса условий не
 * меняет (решение §7.29 п. 6); включение Вебвизора — меняет. API сравнивает
 * отпечатки соседних ревизий в `insertRevision` и при различии ставит
 * `consentVersion = version` (db-design-r3g §4).
 *
 * SB147 (v0.19.65): рядом с Вебвизором — галочка электронной коммерции
 * Метрики (`metrikaEcommerceEnabled`): снимок без ключа и снимок с `true`
 * дают одну строку, снятие галочки — другую. Отпечаток в базе не хранится —
 * обе стороны сравнения считает текущий код, поэтому выкладка сама по себе
 * посетителей не переспрашивает.
 */
export function consentFingerprint(counters: SiteCounters, opts: { textsKey?: string } = {}): string {
  const services = enabledCounterServices(counters);
  // Явный порядок ключей вместо canonicalJson: модуль не тянет snapshot.ts (цикл).
  // `textsKey` — видимые тексты редакции (consentFingerprintWithTexts): тогда
  // условия зависят от смысла текста, а не от номера редакции (uiux §8 п. 4).
  const always = {
    services,
    webvisor: counters.metrika?.webvisor === true,
    ecommerce: metrikaEcommerceEnabled(counters),
    noticeWithoutCounters: counters.noticeWithoutCounters,
    ...(opts.textsKey === undefined ? { platformTextVersion: counters.platformTextVersion } : { texts: opts.textsKey }),
  };
  if (services.length === 0) return JSON.stringify(always);
  return JSON.stringify({ ...always, mode: counters.mode, intro: counters.intro.trim() });
}

/** Сменились ли условия согласия между двумя состояниями части. */
export function consentConditionsChanged(before: SiteCounters, after: SiteCounters): boolean {
  return consentFingerprint(before) !== consentFingerprint(after);
}

// ── «Что изменено» ──

/** Строка «Что изменено» (та же форма, что `SiteViewDiffItem` в diff.ts). */
export interface SiteCountersDiffItem {
  path: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** Подписи полей части COUNTERS. */
export const SITE_COUNTERS_DIFF_LABELS = {
  metrika: "Яндекс Метрика",
  webvisor: "Метрика: Вебвизор",
  ecommerce: "Метрика: данные о товарах и заказах",
  vk: "VK Реклама",
  mode: "Когда включать счётчики",
  noticeWithoutCounters: "Без счётчиков: уведомление о cookie",
  intro: "Вступление баннера cookie",
  platformTextVersion: "Текст баннера от платформы",
} as const satisfies Record<string, string>;

const serviceValue = (entry: { id: string } | null): string => (entry === null ? "выключена" : `номер ${entry.id}`);

/**
 * Подробности различий части COUNTERS словами: «Яндекс Метрика: выключена →
 * номер 12345678», «Метрика: Вебвизор: выключен → включён», «Когда включать
 * счётчики: только после согласия → уведомление». Номера счётчиков не
 * секрет — показываются.
 */
export function diffSiteCounters(before: SiteCounters, after: SiteCounters): SiteCountersDiffItem[] {
  const L = SITE_COUNTERS_DIFF_LABELS;
  const items: SiteCountersDiffItem[] = [];
  const push = (key: keyof typeof L, b: string | null, a: string | null): void => {
    if (b !== a) items.push({ path: `counters.${key}`, label: L[key], before: b, after: a });
  };
  push("metrika", serviceValue(before.metrika), serviceValue(after.metrika));
  const webvisor = (c: SiteCounters): string => (c.metrika?.webvisor === true ? "включён" : "выключен");
  push("webvisor", webvisor(before), webvisor(after));
  // SB147: нет Метрики — «не передавать»; нет ключа у включённой — «передавать».
  const ecommerce = (c: SiteCounters): string => (metrikaEcommerceEnabled(c) ? "передавать" : "не передавать");
  push("ecommerce", ecommerce(before), ecommerce(after));
  push("vk", serviceValue(before.vk), serviceValue(after.vk));
  const mode = (c: SiteCounters): string => SITE_COUNTER_MODE_LABELS[c.mode].toLowerCase();
  push("mode", mode(before), mode(after));
  push(
    "noticeWithoutCounters",
    before.noticeWithoutCounters ? "показывать" : "не показывать",
    after.noticeWithoutCounters ? "показывать" : "не показывать",
  );
  const intro = (c: SiteCounters): string => (c.intro.trim() === "" ? "текст платформы" : c.intro.trim());
  push("intro", intro(before), intro(after));
  push("platformTextVersion", `версия ${before.platformTextVersion}`, `версия ${after.platformTextVersion}`);
  return items;
}

// ── Проверки окна публикации (только предупреждения, SB120; db-design-r3g §10) ──

export type SiteCountersPublishWarningCode =
  | "site_counters_notice_mode"
  | "site_counters_notice_forbidden"
  | "site_counters_privacy_hint"
  | "site_consent_banner_contrast_low";

export interface SiteCountersPublishWarning {
  code: SiteCountersPublishWarningCode;
  level: "warning";
  message: string;
  /** Сервисы, к которым относится подсказка о политике ПДн. */
  services?: SiteCounterService[];
}

export interface SiteCountersPublishInput {
  /** Черновик части COUNTERS. */
  counters: SiteCounters;
  /** Опубликованная часть; null — магазин без публикаций (сравнение с умолчаниями). */
  published: SiteCounters | null;
  /** Тема черновика: цвета и вид баннера. */
  theme: { readonly colors: SiteColors; readonly consentBanner: SiteConsentBanner };
  /** Разрешён ли режим «Уведомление» платформой (`SITE_CONSENT_NOTICE_ALLOWED`). */
  noticeAllowed: boolean;
}

/** «о Яндекс Метрике и VK Рекламе». */
function aboutList(services: readonly SiteCounterService[]): string {
  const names = services.map((s) => COUNTER_SERVICES[s].aboutLabel);
  return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} и ${names[names.length - 1]}`;
}

const ruTenth = (value: number): string => value.toFixed(1).replace(".", ",");

/**
 * Предупреждения окна публикации по части COUNTERS и виду баннера.
 * Публикацию не блокируют (SB120). «Политика ПДн не опубликована» — уже
 * ошибка SB74 и здесь не дублируется (SB137).
 */
export function siteCountersPublishWarnings(input: SiteCountersPublishInput): SiteCountersPublishWarning[] {
  const { counters, theme, noticeAllowed } = input;
  const out: SiteCountersPublishWarning[] = [];
  const services = enabledCounterServices(counters);
  if (services.length > 0 && counters.mode === "notice") {
    out.push(
      noticeAllowed
        ? {
            code: "site_counters_notice_mode",
            level: "warning",
            message:
              "Счётчики будут работать до согласия посетителя (режим «Уведомление»). Юрист ещё не ответил, " +
              "достаточно ли уведомления. Риск — на магазине.",
          }
        : {
            code: "site_counters_notice_forbidden",
            level: "warning",
            message: "Платформа запретила режим «Уведомление» — счётчики включатся только после согласия посетителя.",
          },
    );
  }
  const before = new Set(enabledCounterServices(input.published ?? DEFAULT_SITE_COUNTERS));
  const added = services.filter((s) => !before.has(s));
  if (added.length > 0) {
    out.push({
      code: "site_counters_privacy_hint",
      level: "warning",
      message: `Добавьте в политику ПДн абзац о ${aboutList(added)}.`,
      services: added,
    });
  }
  // Баннер виден, если есть счётчики или включено уведомление без них.
  const bannerShown = services.length > 0 || counters.noticeWithoutCounters;
  const contrast = bannerShown ? consentBannerWarnings(theme.consentBanner, theme)[0] : undefined;
  if (contrast?.value !== undefined) {
    out.push({
      code: "site_consent_banner_contrast_low",
      level: "warning",
      // QA D6: рядом в окне стоит «Контраст цветов темы в норме» — подпись говорит, что речь о баннере и что делать.
      message: `Контраст баннера cookie ниже 4,5:1 (${ruTenth(contrast.value)}:1) — возьмите читаемый цвет текста или заливки.`,
    });
  }
  return out;
}
